import { useEffect, useRef } from 'react'
import { HelpCircle, X } from 'lucide-react'
import clsx from 'clsx'

import { useAyuda } from '@/lib/ayuda'

interface Props {
  titulo: string
  descripcion?: string
  onCerrar: () => void
  children: React.ReactNode
  pie?: React.ReactNode
  ancho?: 'sm' | 'md' | 'lg' | 'xl'
}

const ANCHOS = {
  sm: 'max-w-[420px]',
  md: 'max-w-[560px]',
  lg: 'max-w-[760px]',
  xl: 'max-w-[1000px]',
}

// Diálogo del sistema.
//
// Usa el <dialog> nativo por dos cosas que valen mucho y que a mano se hacen
// mal: el foco queda atrapado adentro (el tabulador no se escapa al fondo) y
// Escape cierra sin que haya que escuchar el teclado.
export function Modal({ titulo, descripcion, onCerrar, children, pie, ancho = 'md' }: Props) {
  const ref = useRef<HTMLDialogElement>(null)
  const abrirAyuda = useAyuda((s) => s.abrir)

  useEffect(() => {
    const dialogo = ref.current
    if (!dialogo) return

    if (!dialogo.open) dialogo.showModal()

    // El foco va al primer campo, y esto tiene que estar ACÁ y no en cada
    // diálogo.
    //
    // Los veinte `autoFocus` que hay repartidos por los formularios del
    // sistema no hacían NADA, y es un caso de manual: React 18 no emite el
    // atributo `autofocus` al DOM —lo saca y llama a `.focus()` él mismo
    // durante el commit—, y en ese momento el `<dialog>` todavía no tiene el
    // atributo `open`, así que la hoja del navegador lo tiene en
    // `display:none` y un elemento que no se renderiza no se puede enfocar.
    // Después `showModal()` corre su propio algoritmo de foco, busca un
    // `[autofocus]` que React nunca escribió, no lo encuentra y enfoca el
    // primer botón. Medido en Chromium: en la ficha de producto y en el cierre
    // de turno el foco caía en un BUTTON, y `dialog[open] [autofocus]`
    // devolvía cero nodos.
    //
    // El síntoma es el que reportó el mostrador: se abre el diálogo para
    // escribir un número y hay que ir al mouse a hacer clic en el campo.
    //
    // Se enfoca el primer CAMPO y no el primer enfocable, que es la
    // diferencia: el primer enfocable de casi todos estos diálogos es el
    // botón de ayuda o el de cerrar del encabezado. Si no hay ningún campo
    // —los diálogos de detalle, que solo se leen— no se toca el foco y el
    // navegador hace lo suyo.
    const campo = dialogo.querySelector<HTMLElement>(
      'input:not([type="hidden"]):not([disabled]):not([readonly]),' +
      'textarea:not([disabled]):not([readonly])',
    )

    campo?.focus()

    // Y esto va en un efecto que corre UNA sola vez, con la lista de
    // dependencias VACÍA.
    //
    // Estaba junto con el listener del Escape, o sea atado a `onCerrar`, y ese
    // callback lo escribe cada pantalla como una flecha adentro del JSX: se
    // recrea en cada render del padre, así que el efecto volvía a correr y el
    // foco saltaba de vuelta al primer campo. En el diálogo de cobro del POS
    // el padre se renderiza cada 350 ms —sondea la balanza— así que el cajero
    // que se movía a otro campo veía el cursor volver solo, y el teclado
    // dejaba de cargar el importe del pago sin ninguna señal. La versión
    // completa: abrir y enfocar es una cosa que pasa al ABRIR; escuchar el
    // Escape es otra que depende del callback de hoy.
  }, [])

  useEffect(() => {
    const dialogo = ref.current
    if (!dialogo) return

    // El evento cancel es el Escape. Se previene el cierre nativo para que el
    // padre controle el estado y no queden desincronizados.
    const alCancelar = (e: Event) => {
      e.preventDefault()
      onCerrar()
    }

    dialogo.addEventListener('cancel', alCancelar)
    return () => dialogo.removeEventListener('cancel', alCancelar)
  }, [onCerrar])

  return (
    <dialog
      ref={ref}
      className={clsx(
        'w-[calc(100vw-2rem)] rounded-xl border border-borde bg-crema p-0 shadow-elevada',
        'backdrop:bg-tinta/45 backdrop:backdrop-blur-[2px]',
        'animate-entrar',

        // El diálogo COMPLETO se acota al alto de la ventana y se reparte por
        // flex: el encabezado y el pie ocupan lo suyo y el cuerpo se queda con
        // el resto, con su propio scroll.
        //
        // Antes se acotaba solo el cuerpo, y en un monitor de 900 px el pie con
        // los botones de guardar quedaba abajo del borde de la pantalla, sin
        // forma de llegar: el diálogo nativo no scrollea.
        // `hidden open:flex`: un <dialog> sin el atributo open lo esconde la
        // hoja del navegador con un display:none de menos especificidad que
        // una clase, así que un `flex` suelto lo mostraría antes de que el
        // efecto llame a showModal.
        'hidden max-h-[calc(100vh-2rem)] flex-col open:flex',
        ANCHOS[ancho],
      )}
      // Click en el fondo cierra. Se compara el target con el propio dialog
      // porque el backdrop no es un elemento aparte.
      onClick={(e) => {
        if (e.target === ref.current) onCerrar()
      }}
    >
      <header className="flex shrink-0 items-start gap-3 border-b border-borde px-5 py-4">
        <div className="min-w-0 flex-1">
          <h2 className="text-[17px] font-bold tracking-[-0.01em] text-tinta">{titulo}</h2>
          {descripcion && (
            <p className="mt-1 text-[13.5px] leading-snug text-tinta-tenue">{descripcion}</p>
          )}
        </div>

        {/* Ayuda del diálogo. Va acá y no solo en el encabezado de la
            pantalla porque un modal tapa el encabezado: el botón de arriba
            queda inalcanzable mientras el diálogo está abierto, y es
            justamente cuando la ayuda del diálogo hace falta.

            El código lo declara el contenido con useAyudaDe(); si no declaró
            ninguno, se muestra el de la pantalla que está debajo. */}
        <button
          type="button"
          onClick={() => abrirAyuda()}
          className="shrink-0 rounded p-1.5 text-tinta-tenue transition-colors hover:bg-crema-alt hover:text-tinta"
          aria-label="Ayuda de este diálogo"
          title="Ayuda (F1)"
        >
          <HelpCircle className="h-[18px] w-[18px]" />
        </button>

        <button
          type="button"
          onClick={onCerrar}
          className="-mr-1.5 -mt-1 rounded p-2 text-tinta-tenue transition-colors hover:bg-crema-alt hover:text-tinta"
          aria-label="Cerrar"
        >
          <X className="h-[18px] w-[18px]" />
        </button>
      </header>

      {/* El cuerpo se queda con el alto que sobra y scrollea solo. */}
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">{children}</div>

      {pie && (
        <footer className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-borde bg-crema-alt/60 px-5 py-3.5">
          {pie}
        </footer>
      )}
    </dialog>
  )
}
